import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchWorkflowsAPI } from '../../services/workflowService';
import {
  fetchWorkflowConfiguration,
  deleteWorkflowById,
} from '../../apis/Workflows';

const toTitleCase = (value = '') => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

const nodeTypeLabel = (type = '') => {
  if (!type) return '';
  const clean = type.replace(/Node$/i, '').trim();
  if (!clean) return '';
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

const buildNodeLabel = (node = {}) => {
  const dataLabel =
    typeof node?.data?.label === 'string' ? node.data.label.trim() : '';
  if (dataLabel) return dataLabel;

  const configLabel =
    typeof node?.data?.config?.label === 'string'
      ? node.data.config.label.trim()
      : '';
  if (configLabel) return configLabel;

  const fallbackLabel =
    typeof node?.label === 'string' ? node.label.trim() : '';
  if (fallbackLabel) return fallbackLabel;

  const connection =
    node?.data?.config?.connection || node?.config?.connection || {};
  const connectorRaw =
    connection?.connector_name ||
    connection?.connector ||
    connection?.type ||
    '';
  const connector = toTitleCase(connectorRaw);
  const typeSuffix = nodeTypeLabel(node?.type);

  if (connector && typeSuffix) return `${connector} (${typeSuffix})`;
  if (connector) return connector;
  if (typeSuffix) return typeSuffix;
  return node?.id || '';
};

const hydrateNodeData = (node = {}) => {
  const rawData =
    node?.data && typeof node.data === 'object'
      ? node.data
      : { label: node?.label || '', config: node?.config || {} };

  // Merge config from both node.config and node.data.config
  const configFromData =
    rawData?.config && typeof rawData.config === 'object'
      ? { ...rawData.config }
      : {};
  const configFromNode =
    typeof node?.config === 'object' ? { ...node.config } : {};

  // Merge both configs, with node.config taking precedence for API-loaded data
  const config = {
    ...configFromData,
    ...configFromNode,
  };

  const data = {
    ...rawData,
    config,
  };

  const label = buildNodeLabel({ ...node, data });

  return {
    ...node,
    // Preserve config on node itself for backward compatibility
    config: {
      ...config,
      label: config.label || label,
    },
    data: {
      ...data,
      label,
      config: {
        ...config,
        label: config.label || label,
      },
    },
  };
};

export const fetchWorkflows = createAsyncThunk(
  'workflows/fetchWorkflows',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchWorkflowsAPI();
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchWorkflowConfigAndSet = createAsyncThunk(
  'workflows/fetchWorkflowConfigAndSet',
  async ({ workflowId, workflowName }, { dispatch, rejectWithValue }) => {
    try {
      const config = await fetchWorkflowConfiguration(workflowId);
      console.log('[DEBUG] Full config from API:', config);

      if (!config || !config.config || !Array.isArray(config.config.nodes)) {
        // Initialize empty workflow
        dispatch(loadWorkflow({ workflowId, nodes: [], edges: [] }));
        return {
          workflowId,
          workflowName,
          nodes: [],
          edges: [],
          entitySelections: {},
          connectionDetails: [],
        };
      }
      const rawNodes = config?.config?.nodes || [];
      const rawEdges = config?.config?.edges || [];
      const entitySelections = config?.config?.entitySelections || {};
      const mappings = config?.config?.mappings || [];
      const featureFlags = config?.config?.featureFlags || {};
      // const workflowId = config?.workflow_id || '';
      const workflowConfigurationId = config?.workflow_configuration_id || '';

      // const transformedNodes = rawNodes.map((node) => ({
      //   id: node.id,
      //   position: node.position,
      //   type: node.type,
      //   data: {
      //     label: `${node.config.label} (${node.type})`,
      //     config: node.config || {},
      //   },
      // }));

      const transformedNodes = rawNodes.map((node) => {
        if (
          node?.data &&
          typeof node.data === 'object' &&
          'label' in node.data &&
          'config' in node.data
        ) {
          const safeConfig =
            node.data.config && typeof node.data.config === 'object'
              ? { ...node.data.config }
              : {};
          return hydrateNodeData({
            ...node,
            data: {
              ...node.data,
              config: safeConfig,
            },
          });
        }

        const fallbackConfig =
          node?.data?.config && typeof node.data.config === 'object'
            ? { ...node.data.config }
            : typeof node?.config === 'object'
              ? { ...node.config }
              : {};

        return hydrateNodeData({
          ...node,
          data: {
            label:
              (typeof node?.data?.label === 'string' && node.data.label) ||
              (typeof node?.label === 'string' && node.label) ||
              fallbackConfig.label ||
              '',
            config: fallbackConfig,
          },
        });
      });

      // Helper function to check if a node is a backtrack node
      const isBacktrackNode = (nodeId) => {
        const node = rawNodes.find((n) => n.id === nodeId);
        return (
          node?.type === 'backtrackNode' || node?.data?.type === 'backtrackNode'
        );
      };

      const transformedEdges = rawEdges.map((edge) => {
        const isBacktrackEdge =
          isBacktrackNode(edge.source) || isBacktrackNode(edge.target);
        const edgeColor = isBacktrackEdge ? '#9c27b6' : '#1976d2';

        return {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          animated: true,
          label: edge.label || '',
          markerEnd: {
            type: 'arrowclosed',
            width: 20,
            height: 20,
            color: edgeColor,
          },
          ...(isBacktrackEdge && {
            style: { stroke: '#9c27b6', strokeWidth: 2 },
          }),
        };
      });

      return {
        // workflowId: config.workflow_id,
        // workflowConfigurationId: config.workflow_configuration_id,
        workflowId,
        workflowConfigurationId,
        workflowName,
        nodes: transformedNodes,
        edges: transformedEdges,
        entitySelections,
        connectionDetails: mappings,
        featureFlags,
      };
    } catch (err) {
      // If error is "not found" or similar, treat as new
      if (
        err.message?.toLowerCase().includes('not found') ||
        err.message?.toLowerCase().includes('no config')
      ) {
        dispatch(loadWorkflow({ workflowId, nodes: [], edges: [] }));
        return {
          workflowId,
          workflowName,
          nodes: [],
          edges: [],
          entitySelections: {},
        };
      }
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const deleteWorkflow = createAsyncThunk(
  'workflows/deleteWorkflow',
  async (workflowId, { rejectWithValue }) => {
    try {
      await deleteWorkflowById(workflowId);
      return workflowId; // Return to reducer for local removal
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const initialState = {
  list: [],
  loading: false,
  error: null,

  workflows: {},
  currentWorkflowName: '',
  nodes: [],
  edges: [],
  unsavedChangesByWorkflow: {},
};

const workflowSlice = createSlice({
  name: 'workflows',
  initialState,
  reducers: {
    setNodes: (state, action) => {
      state.nodes = action.payload;
    },
    setEdges: (state, action) => {
      state.edges = action.payload;
    },

    saveWorkflow: (state, action) => {
      const {
        workflowId,
        nodes,
        edges,
        connectionDetails,
        workflowConfigurationId,
        workflowRunId,
      } = action.payload;
      if (!workflowId) return;
      state.workflows[workflowId] = {
        ...state.workflows[workflowId],
        nodes,
        edges,
        ...(connectionDetails && { connectionDetails }),
        ...(workflowId && { workflowId }),
        ...(workflowConfigurationId && { workflowConfigurationId }),
        ...(workflowRunId && { workflowRunId }),
      };
      state.currentWorkflowName = workflowId;
    },

    loadWorkflow: (state, action) => {
      const { workflowId } = action.payload;

      const stored = state.workflows[workflowId];

      if (stored) {
        state.nodes = stored.nodes || [];
        state.edges = stored.edges || [];
        state.currentWorkflowName = workflowId;

        if (stored.connectionDetails) {
          state.workflows[workflowId].connectionDetails =
            stored.connectionDetails;
        }
      }
    },
    setFinalWorkflowJson: (state, action) => {
      const { workflowId, workflowJson } = action.payload;
      if (!workflowId || !workflowJson) return;

      if (!state.workflows[workflowId]) {
        state.workflows[workflowId] = {};
      }

      state.workflows[workflowId].workflowJson = workflowJson;
    },

    setConnectionDetailsForWorkflow: (state, action) => {
      const { workflowId, connectionDetails } = action.payload;
      if (!state.workflows[workflowId]) state.workflows[workflowId] = {};
      state.workflows[workflowId].connectionDetails = connectionDetails;
    },

    addWorkflow: (state, action) => {
      state.list.push(action.payload);
    },

    resetWorkflows: (state) => {
      state.list = [];
      state.loading = false;
      state.error = null;
      state.workflows = {};
      state.currentWorkflowName = '';
      state.nodes = [];
      state.edges = [];
      state.unsavedChangesByWorkflow = {};
    },
    setScheduleDateTime: (state, action) => {
      const {
        workflowId,
        scheduleDateTime,
        timezone,
        frequency,
        endDate,
        customDays,
        hourInterval,
      } = action.payload;
      if (!state.workflows[workflowId]) state.workflows[workflowId] = {};
      state.workflows[workflowId].scheduleDateTime = scheduleDateTime;
      state.workflows[workflowId].timezone = timezone;
      if (typeof frequency !== 'undefined') {
        state.workflows[workflowId].frequency = frequency;
      }
      if (typeof endDate !== 'undefined') {
        state.workflows[workflowId].endDate = endDate;
      }
      if (typeof customDays !== 'undefined') {
        state.workflows[workflowId].customDays = customDays;
      }
      if (typeof hourInterval !== 'undefined') {
        state.workflows[workflowId].hourInterval = hourInterval;
      }
    },

    setScheduleLockUntil: (state, action) => {
      const { workflowId, lockUntil } = action.payload;
      if (!state.workflows[workflowId]) state.workflows[workflowId] = {};
      state.workflows[workflowId].scheduleLockUntil = lockUntil ?? null;
    },
    setTriggerResponse: (state, action) => {
      const { workflowId, triggerResponse } = action.payload;
      console.log('triggerResponse:', triggerResponse);
      if (!state.workflows[workflowId]) {
        state.workflows[workflowId] = {};
      }
      state.workflows[workflowId].triggerResponse = triggerResponse;
      console.log('triggerResponse:', triggerResponse);
    },

    setScheduledStatus: (state, action) => {
      const { workflowId, scheduledStatus } = action.payload;

      if (!state.workflows[workflowId]) {
        state.workflows[workflowId] = {};
      }

      state.workflows[workflowId].scheduledStatus = scheduledStatus;

      // Save the workflow_run_id separately for easier comparison later
      if (scheduledStatus?.workflow_run_id) {
        state.workflows[workflowId].workflowRunId =
          scheduledStatus.workflow_run_id;
        state.workflows[workflowId].scheduleDateTime = null;
        state.workflows[workflowId].timezone = null;
        // Note: do NOT clear endDate or scheduleLockUntil here
      }
    },
    clearWorkflow(state, action) {
      const workflowId = action.payload;
      if (state.workflows[workflowId]) {
        delete state.workflows[workflowId];
      }
      if (state.currentWorkflowName === workflowId) {
        state.currentWorkflowName = '';
        state.nodes = [];
        state.edges = [];
      }
      if (state.unsavedChangesByWorkflow?.[workflowId] !== undefined) {
        delete state.unsavedChangesByWorkflow[workflowId];
      }
    },
    renameWorkflow: (state, action) => {
      const { workflowId, name } = action.payload;
      const row = state.list?.find((w) => w.workflow_id === workflowId);
      if (row) row.name = name;
      state.workflows[workflowId] ??= {};
      state.workflows[workflowId].name = name;
    },
    markWorkflowDirty: (state, action) => {
      const { workflowId } = action.payload || {};
      if (!workflowId) return;
      state.unsavedChangesByWorkflow ??= {};
      state.unsavedChangesByWorkflow[workflowId] = true;
    },
    clearWorkflowDirty: (state, action) => {
      const { workflowId } = action.payload || {};
      if (!workflowId) return;
      state.unsavedChangesByWorkflow ??= {};
      state.unsavedChangesByWorkflow[workflowId] = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflows.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWorkflowConfigAndSet.fulfilled, (state, action) => {
        const {
          workflowId,
          nodes,
          edges,
          entitySelections,
          connectionDetails,
          workflowConfigurationId,
          featureFlags,
        } = action.payload;
        console.log('✅ Config fetched from backend:', action.payload);
        console.log('REDUCER called! payload:', action.payload);

        state.nodes = nodes;
        state.edges = edges;
        state.currentWorkflowName = workflowId;
        state.workflows[workflowId] = {
          ...state.workflows[workflowId],
          nodes,
          edges,
          entitySelections,
          connectionDetails,
          workflowId,
          workflowConfigurationId,
          featureFlags,
        };
        state.unsavedChangesByWorkflow ??= {};
        state.unsavedChangesByWorkflow[workflowId] = false;
      })
      .addCase(fetchWorkflowConfigAndSet.rejected, (state, action) => {
        console.log('REDUCER called! payload:', action.payload);

        // const workflowName = action.meta?.arg?.workflowName;
        // if(workflowName) delete state.workflows[workflowName];
        state.error =
          action.payload || 'Failed to load workflow configuration.';
        // state.nodes = [];
        // state.edges = [];
        // state.currentWorkflowName = '';
        state.loading = false;
      })
      .addCase(deleteWorkflow.fulfilled, (state, action) => {
        const id = action.payload;
        delete state.workflows[id];
        if (state.currentWorkflowName === id) {
          state.currentWorkflowName = '';
        }
        if (state.unsavedChangesByWorkflow?.[id] !== undefined) {
          delete state.unsavedChangesByWorkflow[id];
        }
      })
      .addCase(deleteWorkflow.rejected, (state, action) => {
        console.error('Failed to delete workflow:', action.payload);
      });
  },
});

export const {
  setNodes,
  setEdges,
  saveWorkflow,
  loadWorkflow,
  setConnectionDetailsForWorkflow,
  setFinalWorkflowJson,
  addWorkflow,
  resetWorkflows,
  setScheduleDateTime,
  setScheduleLockUntil,
  setTriggerResponse,
  setScheduledStatus,
  clearWorkflow,
  renameWorkflow,
  markWorkflowDirty,
  clearWorkflowDirty,
} = workflowSlice.actions;

export default workflowSlice.reducer;
